/**
 * ArcGIS Online OAuth 2.0 Authorization Code flow utilities.
 *
 * Opens a popup for user login, captures the auth code via postMessage
 * from arcgis-oauth-callback.html, then exchanges it for a token
 * through the backend proxy (which holds the client secret).
 */
import axios from 'axios';

const ARCGIS_AUTHORIZE_URL = 'https://www.arcgis.com/sharing/rest/oauth2/authorize';
const CLIENT_ID = process.env.REACT_APP_ARCGIS_CLIENT_ID;
const REDIRECT_URI = `${window.location.origin}/arcgis-oauth-callback.html`;

/**
 * Open a popup window to ArcGIS Online authorization page.
 * Returns a Promise that resolves with { code } on success.
 */
export function openArcGISAuthPopup() {
  return new Promise((resolve, reject) => {
    if (!CLIENT_ID) {
      return reject(new Error('REACT_APP_ARCGIS_CLIENT_ID is not configured'));
    }

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      expiration: '120' // 2 hours
    });

    const url = `${ARCGIS_AUTHORIZE_URL}?${params.toString()}`;
    const width = 600;
    const height = 550;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'arcgis-oauth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup) {
      return reject(new Error('Popup blocked. Please allow popups for this site.'));
    }

    function onMessage(event) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'arcgis-oauth-callback') return;

      window.removeEventListener('message', onMessage);
      clearInterval(pollTimer);

      if (event.data.error) {
        reject(new Error(event.data.error));
      } else if (event.data.code) {
        resolve({ code: event.data.code });
      } else {
        reject(new Error('No authorization code received'));
      }
    }

    window.addEventListener('message', onMessage);

    // Poll for popup being closed manually (user cancelled)
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);
        window.removeEventListener('message', onMessage);
        reject(new Error('Sign-in cancelled'));
      }
    }, 500);
  });
}

/**
 * Exchange an OAuth authorization code for an access token via the backend.
 * @param {string} code - Authorization code from the OAuth redirect
 * @returns {Promise<{ accessToken, username, expiresIn }>}
 */
export async function exchangeCodeForToken(code) {
  const resp = await axios.post('/api/v1/proxy/arcgis-oauth/token', {
    code,
    redirectUri: REDIRECT_URI
  });
  return resp.data?.data;
}
